import { useFlags } from 'launchdarkly-react-client-sdk';

/* Permission flags from LaunchDarkly */
declare module PermissionFlags {

    /* Project specific permission */
    interface Projects {

        /* Permissions for viewing a project's resource planning views or not */
        permissionProjectResourcePlanningView: boolean,

        /* Permissions for viewing a project's project planning views or not */
        permissionProjectPlanningView: boolean,


        /* Permissions for viewing a project's activity map views or not */
        permissionProjectActivityMapViewGrid: boolean,
        permissionProjectActivityMapViewGantt: boolean,

        /* Permissions for updating the project milestones or not */
        permissionProjectDetailsUpdateProjectMilestones: boolean,

        /* Permissions for using the project tools or not */
        permissionProjectToolsView: boolean,

        /* Permissions for view reports/Dashboard page */
        enableReportsPageView: boolean,

        /* Permissions for using / access project templating functions */
        enableProjectTemplating: boolean,

        /* Permissions for creating projects from templating system */
        enableProjectCreateFromTemplate: boolean,
    }

    interface ProjectActivities {

        /* Permissions for updating a project activity start time or not */
        permissionProjectActivityUpdateStartDate: boolean,

        /* Permissions for updating a project activity end time or not */
        permissionProjectActivityUpdateEndDate: boolean,

        /* Permissions for updating project activity hire type associations or not */
        permissionProjectActivityUpdateHireTypes: boolean,

        /* Permissions for updating activity timing by milestone / offset association or not */
        permissionProjectActivityUpdateTimingMilestone: boolean,

        /* Permissions for updating activity duration or not */
        permissionProjectActivityUpdateDuration: boolean,

        /* Permissions for updating activity operational cost */
        permissionProjectActivityUpdateOperationalCost: boolean,

        /* Permissions for updating activity ongoing cost */
        permissionProjectActivityUpdateOngoingCost: boolean,
    }
}

declare module SystemSettingFlags {

    interface FormFields {

        /* Specifies the minimum character length of descriptor fields (text areas describing an entity). */
        systemSettingFormFieldsDescriptorsMinCharacterLength: number,
    }
}

export type PermissionBasedFeatureFlagKeys =
    keyof PermissionFlags.Projects |
    keyof PermissionFlags.ProjectActivities;

export type SystemSettingBasedFeatureFlagKeys =
    keyof SystemSettingFlags.FormFields;

export interface UseFeatureFlagsHook
    extends PermissionFlags.Projects,
        PermissionFlags.ProjectActivities,
        SystemSettingFlags.FormFields {

    /* Flag for allowing the budgeting system to use DEM specified durations for how long an activity cost is ongoing (if enabled), applies for the length of the project if not enabled. */
    useDEMSpecificOngoingCostDuration: boolean,

    /* Flag for whether to capture hiring data that occurs outside the project's timeline or not. If enabled, will include in the budget as hiring costs. */
    includeHiresOutsideProjectTimeline: boolean,

    /* Flag for whether to use and show activity criticality levels or not, as well as any associated UI tools. */
    enableProjectActivityCriticalityViewsAndTools: boolean,

    /* Flag for whether to use and show activity metatags or not, as well as any associated UI tools. */
    enableProjectActivityMetatagViewsAndTools: boolean,

    /* Flag for if the user has ongoing cost editing permissions */
    enableProjectActivityOngoingCostEditing: boolean,

    /* Flag for if the user has operational cost editing permissions */
    enableProjectActivityOperationalCostEditing: boolean,

    /* Flag for if the user has scheduled cost editing permissions */
    enableProjectActivityScheduledCostEditing: boolean,

    /* Flag for if the user has status editing permissions */
    enableProjectActivityStatus: boolean,

    /* Flag for if the user has ability to view Display Options*/
    enableProjectActivityDetailsDisplayOptions: boolean,

    /* Flag for if the user has activity recurrence creation / editing permissions */
    enableProjectActivityRecurrenceEditing: boolean,

    /* Flag for if the user has project rename and duplication permissions */
    enableProjectRenamingOrDuplicationFeatures: boolean,

    /* Flag for if the user has the ability to compare projects*/
    enableProjectCompare: boolean,

    /* Flag for if the user can manipulate project content through power utility tools */
    enableAdHocProjectContentTool: boolean,

    /* Flag for if the user has access to the remap utility tool */
    enableAdHocProjectRemapTool: boolean,

    /* Flag for if the user has access to the version control utility tool */
    enableAdHocProjectVersionControlTool: boolean,

    /* Flag for if the user has access to switch project context control utility tool */
    enableAdHocProjectContextControlTool: boolean,

    /* Flag for if the user has permissions to export Budget by Category data to a CSV file. */
    enableBudgetByCategoryCsvDataExportTool: boolean,

    /* Flag for if the user has permissions to export Budget by Hire data to a CSV file. */
    enableBudgetByHireCsvDataExportTool: boolean,

    /* Flag for if the user has permissions to create and edit custom budget items. */
    enableBudgetByCategoryCustomDataEditing: boolean,

    /* Flag for if the user has permissions to create and edit multiple groups of custom budget items within a category. */
    enableBudgetByCategoryCustomDataMultipleGroups: boolean,

    /* Flag for if the user has permissions to create and edit multiple custom budget items within a subcategory. */
    enableBudgetByCategoryCustomDataMultipleItems: boolean,

    /* Flag for if the user has permissions to access the Adjust Cost Filter tool for Category. */
    enableBudgetByCategoryAdjustCostFilter: boolean,

    /* Flag for if the user has permissions to access the Adjust Cost Filter tool for Hires. */
    enableBudgetByHireAdjustCostFilter: boolean,

    /* Flag for if the user can see hire type descriptions set in the DEM or not. */
    displayHiringTypeDescriptions: boolean,

    /* Flag for if users can create a custom start date for a project to schedule activities against or not. */
    enableCustomCommercializationStartDateEditing: boolean,

    /* Flag for if the user can download xls for a project */
    enableExportProjectXlsTool: boolean

    /* Flag for whether a project's budget will generate fee based cost occurrences that correlate to recurring activities with ongoing cost specified or not. */
    generateOngoingCostOccurrencesForRecurringActivities: boolean,

    /* Flag for enabling the planning workflow for onboarding */
    enablePlanningWorkflowOnboarding: boolean

    /* Flag for enabling the planning workflow for project modifications */
    enablePlanningWorkflowProjectModification: boolean

    /* Flag for enabling the planning workflow for incremental planning */
    enablePlanningWorkflowIncrementalPlanning: boolean,

    /* Flag for enabling the re-scheduling of a sub objective */
    enableRoadmapSubobjectiveTimingRescheduling: boolean,

    /* Flag for enabling the management of Hires */
    enableHireTypeManager: boolean,

    /* Flag for enabling the ability to access the customer milestone editor */
    enableProjectDetailsCustomMilestoneEditor: boolean,

    /* Flag for enabling the ability to export activity map as MS Project file (Bryntum Gantt tooling) */
    enableProjectActivityMapGanttMSProjectExport: boolean,

    /*Flag for enabling subscriptions */
    enableOrganizationSubscriptions: boolean,

    /*Flag for enabling SubObjective Edit Menu */
    enableRoadMapSubObjectiveEditMenu: boolean,

    /*Flag for enabling multiple objectives for one activity */
    enableMultipleObjectiveSupportForActivities: boolean,

    /*Flag for enabling power functionality within the Gantt views  */
    enableGanttPowerTools: boolean,

    /*Flag for enabling admin functionality within the Gantt views  */
    enableGanttAdminTools: boolean,

    /* Flag for enabling reports admin functionality */
    enableReportsAdmin: boolean,

    /* Flag for enabling critical path gantt functionality */
    enableGanttCriticalPathFeature: boolean,

    /* Flag to view import data dialog */
    enableImportProjectData: boolean,

    /* Flag for enabling the AI assistant feature app-wide */
    enableAiAssistant: boolean,

}

export const useFeatureFlags = (): UseFeatureFlagsHook => {

    let featureFlags = useFlags();

    return {
        useDEMSpecificOngoingCostDuration:                    featureFlags[ 'useDemSpecificOngoingCostDuration' ],
        displayHiringTypeDescriptions:                        featureFlags[ 'displayHiringTypeDescriptions' ],
        generateOngoingCostOccurrencesForRecurringActivities: featureFlags[ 'generateOngoingCostOccurrencesForRecurringActivities' ],
        includeHiresOutsideProjectTimeline:                   featureFlags[ 'includeHiresOutsideOfProjectTimelineOnBudgetSummary' ],

        enableProjectActivityCriticalityViewsAndTools:  featureFlags[ 'enableProjectActivityCriticalityViewsAndTools' ],
        enableProjectActivityMetatagViewsAndTools:      featureFlags[ 'enableProjectActivityMetatagViewsAndTools' ],
        enableProjectActivityOngoingCostEditing:        featureFlags[ 'enableProjectActivityOngoingCostEditing' ],
        enableProjectActivityOperationalCostEditing:    featureFlags[ 'enableProjectActivityOperationalCostEditing' ],
        enableProjectActivityScheduledCostEditing:      featureFlags[ 'enableProjectActivityScheduledCostEditing' ],
        enableProjectActivityStatus:                    featureFlags[ 'enableProjectActivityStatus' ],
        enableProjectActivityDetailsDisplayOptions:     featureFlags[ 'enableProjectActivityDetailsDisplayOptions' ],
        enableProjectRenamingOrDuplicationFeatures:     featureFlags[ 'enableProjectRenamingOrDuplicationFeatures' ],
        enableProjectCompare:                           featureFlags[ 'enableProjectCompare' ],
        enableAdHocProjectRemapTool:                    featureFlags[ 'enableAdHocProjectRemapTool' ],
        enableAdHocProjectVersionControlTool:           featureFlags[ 'enableAdHocProjectVersionControlTool' ],
        enableAdHocProjectContextControlTool:           featureFlags[ 'enableAdHocProjectContextControlTool' ],
        enableAdHocProjectContentTool:                  featureFlags[ 'enableAdHocProjectContentTool' ],
        enableBudgetByCategoryCsvDataExportTool:        featureFlags[ 'enableBudgetByCategoryCsvDataExportTool' ],
        enableBudgetByHireCsvDataExportTool:            featureFlags[ 'enableBudgetByHireCsvDataExportTool' ],
        enableBudgetByCategoryCustomDataEditing:        featureFlags[ 'enableBudgetByCategoryCustomDataEditing' ],
        enableBudgetByCategoryCustomDataMultipleGroups: featureFlags[ 'enableBudgetByCategoryCustomDataMultipleGroups' ],
        enableBudgetByCategoryCustomDataMultipleItems:  featureFlags[ 'enableBudgetByCategoryCustomDataMultipleItems' ],
        enableBudgetByCategoryAdjustCostFilter:         featureFlags [ 'enableBudgetByCategoryAdjustCostFilter' ],
        enableBudgetByHireAdjustCostFilter:             featureFlags[ 'enableBudgetByHireAdjustCostFilter' ],
        enableProjectActivityRecurrenceEditing:         featureFlags[ 'enableProjectActivityRecurrenceEditing' ],
        enableCustomCommercializationStartDateEditing:  featureFlags[ 'enableCustomCommercializationStartDateEditing' ],
        enableExportProjectXlsTool:                     featureFlags[ 'enableExportProjectXlsTool' ],
        enablePlanningWorkflowOnboarding:               featureFlags[ 'enablePlanningWorkflowOnboarding' ],
        enablePlanningWorkflowProjectModification:      featureFlags[ 'enablePlanningWorkflowProjectModification' ],
        enablePlanningWorkflowIncrementalPlanning:      featureFlags[ 'enablePlanningWorkflowIncrementalPlanning' ],
        enableRoadmapSubobjectiveTimingRescheduling:    featureFlags[ 'enableRoadmapSubobjectiveTimingRescheduling' ],
        enableRoadMapSubObjectiveEditMenu:              featureFlags[ 'enableRoadmapSubobjectiveEditMenu' ],
        enableHireTypeManager:                          featureFlags[ 'enableHireTypeManager' ],
        enableProjectDetailsCustomMilestoneEditor:      featureFlags [ 'enableProjectDetailsCustomMilestoneEditor' ],
        enableProjectActivityMapGanttMSProjectExport:   featureFlags[ 'enableProjectActivityMapGanttMsProjectExport' ],
        enableMultipleObjectiveSupportForActivities:    featureFlags[ 'enableActivityMultipleObjectiveSupport' ],
        enableOrganizationSubscriptions:                featureFlags[ 'enableOrganizationSubscription' ],
        enableGanttPowerTools:                          featureFlags[ 'enableGanttPowerTools' ],
        enableGanttAdminTools:                          featureFlags[ 'enableGanttAdminTools' ],
        enableReportsPageView:                          featureFlags[ 'enableReportsPageView' ],
        enableReportsAdmin:                             featureFlags[ 'enableReportsAdmin' ],
        enableGanttCriticalPathFeature:                 featureFlags[ 'enableGanttCriticalPathFeature' ],
        enableProjectTemplating:                        featureFlags[ 'enableProjectTemplating' ],
        enableProjectCreateFromTemplate:                featureFlags[ 'enableProjectCreateFromTemplate' ],
        enableImportProjectData:                        featureFlags['enableImportProjectData'],
        enableAiAssistant:                              featureFlags['enableAiAssistant'],

        permissionProjectDetailsUpdateProjectMilestones: featureFlags[ 'permissionProjectDetailsUpdateProjectMilestones' ],
        permissionProjectActivityUpdateStartDate:        featureFlags[ 'permissionProjectActivityUpdateStartDate' ],
        permissionProjectActivityUpdateEndDate:          featureFlags[ 'permissionProjectActivityUpdateEndDate' ],
        permissionProjectResourcePlanningView:           featureFlags[ 'permissionProjectResourcePlanningView' ],
        permissionProjectActivityMapViewGrid:            featureFlags[ 'permissionProjectActivityMapView' ]?.grid ?? false,
        permissionProjectActivityMapViewGantt:           featureFlags[ 'permissionProjectActivityMapView' ]?.gantt ?? false,
        permissionProjectActivityUpdateHireTypes:        featureFlags[ 'permissionProjectActivityUpdateHireTypes' ],
        permissionProjectActivityUpdateOperationalCost:  featureFlags[ 'permissionProjectActivityUpdateOperationalCost' ],
        permissionProjectActivityUpdateOngoingCost:      featureFlags[ 'permissionProjectActivityUpdateOngoingCost' ],
        permissionProjectActivityUpdateTimingMilestone:  featureFlags[ 'permissionProjectActivityUpdateTimingMilestone' ],
        permissionProjectActivityUpdateDuration:         featureFlags[ 'permissionProjectActivityUpdateDuration' ],
        permissionProjectToolsView:                      featureFlags[ 'permissionProjectToolsView' ],
        permissionProjectPlanningView:                   featureFlags[ 'permissionProjectPlanningView' ],

        systemSettingFormFieldsDescriptorsMinCharacterLength: featureFlags[ 'systemSettingFormFieldsDescriptorsMinCharacterLength' ] ?? 20,
    }
};

